import {ServiceBase} from "../service-base";
import {APP_ROOT_ELEMENT_ID, IApplicationService} from "./application.service.interface";
import {IServiceFactory} from "../service-factory.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {ResizeObserver} from "@juggle/resize-observer";
import {NullableNumber} from "../../types/nullable-types";
import {VERTICAL_SPACING} from "../../components/css-global-constants";
import {ApplicationEnvironmentTypeEnum} from "../../env.configs.interface";

export class ApplicationService extends ServiceBase implements IApplicationService {
    constructor(services: IServiceFactory) {
        super(services);

        this._appRootElement = this.services.document.getElementById(APP_ROOT_ELEMENT_ID)!;
        this._updateOriginalHeight();
        this._rootElementResizeObserver = new ResizeObserver(this._onRootElementResized);
        this._rootElementResizeObserver.observe(this._appRootElement);


        makeObservable(this, {
            isActive: observable.ref,
            _originalHeight: observable.ref
        });

    }
    isActive = true;

    private readonly _appRootElement: HTMLElement;
    private readonly _rootElementResizeObserver: ResizeObserver;

    _originalHeight: number = 0;
    get originalHeight(): number {
        return this._originalHeight;
    }

    set originalHeight(value: number) {
        runInAction(() => this._originalHeight = value);
    }

    private _currentCustomHeight: NullableNumber = null;


    get isInMobileView(): boolean {
        return this.services.mediaQuery.sAndBelow;
    }


    private _resizeTimeoutRef: any = null;
    private _onRootElementResized = () => {
        if(this._resizeTimeoutRef) {
            clearTimeout(this._resizeTimeoutRef);
        }
        this._resizeTimeoutRef = setTimeout(() => {
            this._updateOriginalHeight();
            this._resizeTimeoutRef = null;
        }, 500);

    }

    private _updateOriginalHeight(): void {
        this.originalHeight = this._appRootElement.scrollHeight;
        this._sendResizeMessage();
    }

    private _lastSentHeight: NullableNumber = null;
    private _sendResizeMessage(): void {
        let newHeight = this.originalHeight;
        if(this._currentCustomHeight) {
            newHeight = Math.max(this.originalHeight, this._currentCustomHeight);
        }

        newHeight += VERTICAL_SPACING;

        const msg = {
            topic: 'content-resized',
            content: newHeight + 'px'
        };

        if(this._lastSentHeight !== newHeight) {
            this.services.logger.debug('Sending message', msg);
            this._lastSentHeight = newHeight;
            this.services.window.postMessage(msg);
        }
    }

    setCustomHeight(height: number): void {
        const newHeight = Math.floor(height) + 1;
        if(newHeight !== this._currentCustomHeight) {
            this._currentCustomHeight = newHeight;
            this._sendResizeMessage();
        }
    }

    restoreOriginalHeight(): void {
        this._currentCustomHeight = null;
        this._sendResizeMessage();
    }

    sendEnvironmentTypeEvent(environmentType: ApplicationEnvironmentTypeEnum): void {
        if(environmentType === ApplicationEnvironmentTypeEnum.Production) {
            return;
        }

        const getEnvironmentName = () => {
            switch (environmentType) {
                case ApplicationEnvironmentTypeEnum.Test:
                    return 'test';
                case ApplicationEnvironmentTypeEnum.Stage:
                    return 'stage';
                default:
                    return 'prod';
            }
        }

        const msg = {
            topic: 'set-environment',
            content: getEnvironmentName()
        };

        this.services.logger.debug('Sending message', msg);
        this.services.window.postMessage(msg);
    }
}
